/** @module components/cloze-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import View from '@lernetz/ts-lib/src/core/view/View';

import {Span} from './Span';
import {SelectSpanChoice, SelectSpanChoiceView} from './SelectSpanChoice';

import {CanShowCorrectness} from '../quiz';

import {SelectSpan as template} from '../../templates/cloze-quiz';


/**
 * Specifies the initial state for a [[SelectSpan]].
 */
export interface SelectSpanInitialState {

    /**
     * Sets the choices that are available in this [[SelectSpan]].
     */
    choices: SelectSpanChoice[];

    /**
     * Sets whether the correctness of the selected choice should be initially shown.
     * Defaults to `false`.
     */
    showCorrectness?: boolean;
}


/**
 * An `<option>` drop-down [[Span]] in a cloze quiz [[Paragraph]].
 */
export class SelectSpan extends Model implements Span, CanShowCorrectness {

    /**
     * Initializes a new instance of the [[SelectSpan]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional arbitrary object that gets forwarded to [[Model]]'s constructor.
     */
    constructor(initialState: SelectSpanInitialState, data: object = {}) {
        super({
            choices: [],
            selectedChoice: null,
            showCorrectness: false,
            ...initialState,
            ...data
        });

        this.choices.filter(ch => ch.selected).forEach(ch => this.onChoiceSelected(ch));
    }

    /**
     * Gets the choices that are available in this [[SelectSpan]].
     */
    public readonly choices: SelectSpanChoice[];

    /**
     * Gets the currently selected choice.
     */
    public readonly selectedChoice: SelectSpanChoice;

    /**
     * Gets or sets whether the correctness of the selected choice should be shown.
     */
    public showCorrectness: boolean;

    /**
     * Callback method that updates all [[choices]]' [[selected]] state
     * when the specified choice ([[selectedChoice]]) has been selected.
     *
     * @param choice The choice that should become the only currently selected one.
     */
    public onChoiceSelected(choice: SelectSpanChoice) {
        this.set('selectedChoice', choice);
        for (const ch of this.choices) {
            ch.selected = ch === choice;
        }
    }
}


/**
 * View class accompanying [[SelectSpan]] which translates DOM events of the
 * `<select>` element from the default template to [[SelectSpan.onChoiceSelected]] calls
 * on the model.
 *
 * You typically won't have to replace this class. Instead, apply CSS styling to the
 * default template or use a different [[Span]] type altogether.
 */
export class SelectSpanView extends View<SelectSpan> {

    public constructor() {
        super({
            template,
            registrations: {
                'Choice': SelectSpanChoiceView,
            },
        });
    }

    protected init(): void {
        this.getSelectElement().addEventListener('change', this.onValueChanged.bind(this));
    }

    private getSelectElement(): HTMLSelectElement {
        return <HTMLSelectElement> this.node.querySelector('select');
    }

    private onValueChanged(): void {
        const selectElement = this.getSelectElement();
        const selectedIndex = selectElement.selectedIndex;
        if (selectedIndex >= 0) {
            const optionNode = selectElement.querySelector(`option:nth-of-type(${selectElement.selectedIndex + 1})`);
            if ('_lnview' in optionNode) {
                const choice: SelectSpanChoice = optionNode['_lnview']['data'];
                this.data.onChoiceSelected(choice);
            }
        }
    }
}
